import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { materialsAPI } from '../services/api';
import {
    FileText,
    Upload,
    Trash2,
    Search,
    X,
    AlertCircle,
    CheckCircle,
    BookOpen,
    Clock,
    File,
    Plus,
    Eye,
    Loader
} from 'lucide-react';

const TeacherMaterials = () => {
    const [materials, setMaterials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [uploadType, setUploadType] = useState('text');

    const [formData, setFormData] = useState({
        title: '',
        subject: '',
        topic: '',
        description: '',
        content: ''
    });

    const [pdfFile, setPdfFile] = useState(null);

    useEffect(() => {
        fetchMaterials();
    }, []);

    const fetchMaterials = async () => {
        try {
            const response = await materialsAPI.getMyMaterials();
            setMaterials(response.data.materials || []);
        } catch (error) {
            console.error('Error fetching materials:', error);
            setError('Failed to load materials');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setFormLoading(true);

        try {
            if (uploadType === 'pdf' && pdfFile) {
                const reader = new FileReader();
                reader.onload = async () => {
                    const base64 = reader.result.split(',')[1];
                    await materialsAPI.uploadPDF({
                        ...formData,
                        pdfBase64: base64,
                        filename: pdfFile.name
                    });
                    setSuccess('PDF uploaded successfully! Processing in background.');
                    resetForm();
                    fetchMaterials();
                };
                reader.readAsDataURL(pdfFile);
            } else {
                await materialsAPI.upload({
                    ...formData,
                    type: 'text'
                });
                setSuccess('Material uploaded successfully! Processing in background.');
                resetForm();
                fetchMaterials();
            }
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to upload material');
        } finally {
            setFormLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({ title: '', subject: '', topic: '', description: '', content: '' });
        setPdfFile(null);
        setShowModal(false);
        setUploadType('text');
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

        try {
            await materialsAPI.delete(id);
            setSuccess('Material deleted successfully');
            fetchMaterials();
        } catch (error) {
            setError(error.response?.data?.message || 'Failed to delete material');
        }
    };

    const handleView = async (material) => {
        try {
            const response = await materialsAPI.getById(material._id);
            setSelectedMaterial(response.data);
            setShowViewModal(true);
        } catch (error) {
            setError('Failed to load material details');
        }
    };

    const filteredMaterials = materials.filter(m =>
        m.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.topic?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <DashboardLayout>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Course Materials</h1>
                        <p className="text-gray-600 mt-1">Upload and manage your teaching materials</p>
                    </div>
                    <button
                        onClick={() => setShowModal(true)}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all shadow-md"
                    >
                        <Plus className="w-5 h-5" />
                        Upload Material
                    </button>
                </div>

                {/* Stats */}
                <div className="bg-gradient-to-br from-gray-800 via-gray-700 to-gray-900 rounded-2xl p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-300 text-sm font-medium">Total Materials</p>
                            <p className="text-4xl font-bold mt-1">{materials.length}</p>
                            <p className="text-gray-300 text-sm mt-2">Course materials uploaded</p>
                        </div>
                        <div className="p-4 bg-white/90 backdrop-blur rounded-2xl shadow-lg">
                            <BookOpen className="w-10 h-10 text-gray-800" />
                        </div>
                    </div>
                </div>

                {/* Alerts */}
                {error && (
                    <div className="p-4 bg-gray-100 border border-gray-300 rounded-xl flex items-center gap-3 text-gray-700">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{error}</span>
                        <button onClick={() => setError('')} className="ml-auto p-1 hover:bg-gray-200 rounded-lg">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}
                {success && (
                    <div className="p-4 bg-gray-100 border border-gray-300 rounded-xl flex items-center gap-3 text-gray-700">
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{success}</span>
                        <button onClick={() => setSuccess('')} className="ml-auto p-1 hover:bg-gray-200 rounded-lg">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Search */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search materials by title, subject, or topic..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
                        />
                    </div>
                </div>

                {/* Materials Grid */}
                {loading ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                        <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto"></div>
                        <p className="text-gray-500 mt-4">Loading materials...</p>
                    </div>
                ) : filteredMaterials.length === 0 ? (
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900">No materials found</h3>
                        <p className="text-gray-500 mt-1">
                            {searchTerm ? 'Try a different search term' : 'Upload your first course material'}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filteredMaterials.map((material) => (
                            <div
                                key={material._id}
                                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-5 hover:shadow-lg hover:border-gray-300 transition-all group"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="p-3 bg-gray-100 rounded-xl">
                                        {material.type === 'pdf' ? (
                                            <File className="w-6 h-6 text-gray-700" />
                                        ) : (
                                            <FileText className="w-6 h-6 text-gray-700" />
                                        )}
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {material.isProcessed ? (
                                            <span className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                                                Ready
                                            </span>
                                        ) : (
                                            <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-full flex items-center gap-1">
                                                <Loader className="w-3 h-3 animate-spin" />
                                                Processing
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <h3 className="font-semibold text-gray-900 mb-1 line-clamp-1">{material.title}</h3>
                                <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{material.subject}</span>
                                    <span>•</span>
                                    <span>{material.topic}</span>
                                </div>

                                {material.description && (
                                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">{material.description}</p>
                                )}

                                <div className="flex items-center justify-between text-xs text-gray-400">
                                    <div className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        {new Date(material.createdAt).toLocaleDateString()}
                                    </div>
                                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => handleView(material)}
                                            className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(material._id, material.title)}
                                            className="p-2 text-gray-500 hover:text-black hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Upload Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-gray-100 rounded-xl">
                                    <Upload className="w-6 h-6 text-gray-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-gray-900">Upload Course Material</h2>
                            </div>
                            <button
                                onClick={() => resetForm()}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            {/* Upload Type */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Material Type</label>
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setUploadType('text')}
                                        className={`flex-1 p-4 rounded-xl border-2 transition-all ${uploadType === 'text'
                                            ? 'border-black bg-gray-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <FileText className="w-6 h-6 mx-auto mb-2" />
                                        <p className="font-medium">Text Notes</p>
                                        <p className="text-xs text-gray-500">Paste your content</p>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setUploadType('pdf')}
                                        className={`flex-1 p-4 rounded-xl border-2 transition-all ${uploadType === 'pdf'
                                            ? 'border-black bg-gray-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <File className="w-6 h-6 mx-auto mb-2" />
                                        <p className="font-medium">PDF Document</p>
                                        <p className="text-xs text-gray-500">Upload a file</p>
                                    </button>
                                </div>
                            </div>

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Title</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    required
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
                                    placeholder="e.g., Introduction to Calculus"
                                />
                            </div>

                            {/* Subject & Topic */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Subject</label>
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
                                        placeholder="e.g., Mathematics"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Topic</label>
                                    <input
                                        type="text"
                                        value={formData.topic}
                                        onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                                        required
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all"
                                        placeholder="e.g., Derivatives"
                                    />
                                </div>
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">Description (Optional)</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={2}
                                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all resize-none"
                                    placeholder="Brief description of this material..."
                                />
                            </div>

                            {/* Content or PDF Upload */}
                            {uploadType === 'text' ? (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Content</label>
                                    <textarea
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        required
                                        rows={8}
                                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:ring-2 focus:ring-black focus:border-transparent outline-none transition-all resize-none"
                                        placeholder="Paste your course material content here..."
                                    />
                                </div>
                            ) : (
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">PDF File</label>
                                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors">
                                        {pdfFile ? (
                                            <div className="flex items-center justify-center gap-3">
                                                <File className="w-8 h-8 text-gray-600" />
                                                <div className="text-left">
                                                    <p className="font-medium text-gray-900">{pdfFile.name}</p>
                                                    <p className="text-sm text-gray-500">{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setPdfFile(null)}
                                                    className="p-2 text-gray-400 hover:text-gray-600"
                                                >
                                                    <X className="w-5 h-5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer">
                                                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                                                <p className="font-medium text-gray-700">Click to upload PDF</p>
                                                <p className="text-sm text-gray-500">or drag and drop</p>
                                                <input
                                                    type="file"
                                                    accept=".pdf"
                                                    onChange={(e) => setPdfFile(e.target.files[0])}
                                                    className="hidden"
                                                />
                                            </label>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Submit */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => resetForm()}
                                    className="flex-1 px-4 py-3 border border-gray-200 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={formLoading || (uploadType === 'pdf' && !pdfFile) || (uploadType === 'text' && !formData.content)}
                                    className="flex-1 px-4 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl hover:from-gray-700 hover:to-gray-800 transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-md"
                                >
                                    {formLoading ? (
                                        <>
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-5 h-5" />
                                            Upload Material
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Material Modal */}
            {showViewModal && selectedMaterial && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between p-6 border-b border-gray-200">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">{selectedMaterial.material?.title}</h2>
                                <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                                    <span>{selectedMaterial.material?.subject}</span>
                                    <span>•</span>
                                    <span>{selectedMaterial.material?.topic}</span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowViewModal(false)}
                                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="flex items-center gap-4 mb-4 text-sm text-gray-500">
                                <span>{selectedMaterial.wordCount} words</span>
                                <span>•</span>
                                <span>{selectedMaterial.readingTime} min read</span>
                            </div>
                            <div className="prose prose-sm max-w-none">
                                <pre className="whitespace-pre-wrap text-sm text-gray-700 bg-gray-50 p-4 rounded-xl">
                                    {selectedMaterial.material?.content}
                                </pre>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </DashboardLayout>
    );
};

export default TeacherMaterials;
